// src/components/DashboardHeader.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Divider,
  ListItemIcon,
  Tooltip,
  Chip,
  Button
} from '@mui/material';
import {
  Bell,
  User,
  LogOut,
  Settings,
  CircleHelpIcon,
  Users,
  CreditCard,
  Home
} from 'lucide-react';
import {
  User as UserIcon,
  Info as InfoIcon,
  BarChart as BarChartIcon,
  Check as CheckIcon
} from 'lucide-react';
import logo from './logo.png';

const DashboardHeader = ({ userName, userRole, notifications, onLogout, avatarUrl }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);

  const handleOpenUserMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenNotificationsMenu = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleCloseNotificationsMenu = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Close the menu first
      handleCloseUserMenu();

      console.log("Logging out, onLogout function exists:", !!onLogout);

      // Call the provided logout handler if it exists
      if (typeof onLogout === 'function') {
        await onLogout();
      } else {
        console.warn("No logout function provided to DashboardHeader");
        // Fallback - navigate to login page if no logout handler provided
        navigate('/profiles/login');
      }
    } catch (error) {
      console.error("Error during logout:", error);
      // Fallback navigation in case of error
      navigate('/profiles/login');
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleCloseUserMenu();
  };

  const handleBillingClick = () => {
    if (userRole === 'admin' || userRole === 'superadmin') {
      navigate('/profiles/admin/billing');
    } else {
      navigate('/profiles/billing');
    }
  };
  // Added a specific Help Center navigation handler
  const handleHelpCenter = () => {
    handleNavigate('/profiles/help');
  };

  // Added specific help section navigation handlers
  const handleHelpFAQs = () => {
    handleNavigate('/profiles/help/faqs');
  };

  const handleGettingStarted = () => {
    handleNavigate('/profiles/help/getting-started');
  };

  // Function to get role-specific chip properties
  const getRoleChipProps = () => {
    switch (userRole) {
      case 'superadmin':
        return {
          label: 'SuperAdmin',
          color: 'error',
          icon: <Users size={14} />,
          sx: { ml: 2 }
        };
      case 'admin':
        return {
          label: 'Admin',
          color: 'primary',
          icon: <Users size={14} />,
          sx: { ml: 2 }
        };
      default:
        return {
          label: 'User',
          color: 'default',
          icon: <User size={14} />,
          sx: { ml: 2 }
        };
    }
  };

  // Function to get avatar background color based on role
  const getAvatarBgColor = () => {
    switch (userRole) {
      case 'superadmin':
        return '#dc2626'; // Error red for SuperAdmin
      case 'admin':
        return '#2563eb'; // Primary blue for Admin
      default:
        return '#64748b'; // Default gray for regular users
    }
  };

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={logo}
            alt="KoorAI Pro Logo"
            sx={{
              height: 28,
              mr: 2
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 'bold',
              color: '#1a365d',
              mr: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Apps
          </Typography>

          {/* Display role badge if available */}
          {userRole && (
            <Chip
              {...getRoleChipProps()}
            />
          )}
        </Box>

        {/* Right Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Home Button */}
          <Tooltip title="Home - All Modules">
            <IconButton
              onClick={() => navigate('/profiles/home')}
              size="large"
              color="inherit"
            >
              <Home />
            </IconButton>
          </Tooltip>

          {/* Billing Icon for users and admin users */}
          <Tooltip title="Billing & Subscriptions">
            <IconButton
              onClick={() => {
                // Different routes based on user role
                if (userRole === 'admin' || userRole === 'superadmin') {
                  navigate('/profiles/admin/billing');
                } else {
                  navigate('/profiles/billing');
                }
              }}
              size="large"
              color="inherit"
            >
              <CreditCard />
            </IconButton>
          </Tooltip>

          {/* Help Center Button */}
          <Tooltip title="Help Center">
            <IconButton
              onClick={handleHelpCenter}
              // onClick={() => navigate('/profiles/help')}
              size="large"
              color="inherit"
            >
              <CircleHelpIcon />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              onClick={handleOpenNotificationsMenu}
              size="large"
              color="inherit"
            >
              <Badge badgeContent={notifications || 0} color="error">
                <Bell />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Profile */}
          <Tooltip title="User Profile">
            <IconButton
              onClick={handleOpenUserMenu}
              size="large"
              color="inherit"
              sx={{ ml: 1 }}
            >
              <Avatar
                alt={userName}
                src={avatarUrl ? `${process.env.REACT_APP_API_URL}${avatarUrl}` : undefined}
                sx={{
                  bgcolor: avatarUrl ? 'transparent' : getAvatarBgColor(),
                  width: 36,
                  height: 36
                }}
              >
                {!avatarUrl && (userName ? userName[0].toUpperCase() : 'U')}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationsAnchorEl}
          open={Boolean(notificationsAnchorEl)}
          onClose={handleCloseNotificationsMenu}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              width: 380, // Slightly wider
              maxHeight: '500px', // Add max height
              borderRadius: 2,
              overflow: 'auto', // Add scrolling if needed
              '& .MuiMenuItem-root': {
                py: 2, // Increased padding
                borderBottom: '1px solid', // Subtle separator
                borderBottomColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none'
                }
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            pb: 1
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Notifications
            </Typography>
            <Tooltip title="Mark all as read">
              <IconButton size="small">
                <CheckIcon size={18} />
              </IconButton>
            </Tooltip>
          </Box>

          {[
            {
              message: 'Profile Manager with QR and Signature Generators are now available in release 1.1',
              time: '2 minutes ago',
              icon: <UserIcon size={20} />,
              type: 'update'
            },
            {
              message: 'Ensure Basic Profile Info tab is updated to make full use of the application',
              time: '3 hours ago',
              icon: <InfoIcon size={20} />,
              type: 'info'
            },
            {
              message: 'New Module: Net Worth Manager is now available in release 1.1 Key Features Monthly Budget, Actual Transactions, Reports and Common Settings',
              time: '1 day ago',
              icon: <BarChartIcon size={20} />,
              type: 'feature'
            }
          ].map((notification, index) => (
            <MenuItem
              key={index}
              onClick={handleCloseNotificationsMenu}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 2,
                width: '100%'
              }}>
                {/* Notification Icon */}
                <Box sx={{
                  bgcolor:
                    notification.type === 'update' ? 'primary.light' :
                      notification.type === 'info' ? 'warning.light' :
                        'success.light',
                  color:
                    notification.type === 'update' ? 'primary.main' :
                      notification.type === 'info' ? 'warning.main' :
                        'success.main',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.5
                }}>
                  {notification.icon}
                </Box>

                {/* Notification Content */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      mb: 0.5,
                      wordWrap: 'break-word', // Ensures long words break
                      overflowWrap: 'break-word', // Additional support for word breaking
                      whiteSpace: 'normal' // Allows text to wrap to next line
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {notification.time}
                  </Typography>
                </Box>

                {/* Unread Indicator */}
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    mt: 1
                  }}
                />
              </Box>
            </MenuItem>
          ))}

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 2,
            borderTop: '1px solid',
            borderTopColor: 'divider'
          }}>
            <Button
              variant="text"
              color="primary"
              onClick={handleCloseNotificationsMenu}
              fullWidth
            >
              View All Notifications
            </Button>
          </Box>
        </Menu>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseUserMenu}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              width: 220,
              borderRadius: 2,
              '& .MuiMenuItem-root': {
                py: 1.5
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontWeight: 'bold' }}>{userName}</Typography>
            <Typography variant="body2" color="textSecondary">
              {userRole === 'superadmin' ? 'Super Administrator' : userRole === 'admin' ? 'Administrator' : 'Regular User'}
            </Typography>
          </Box>
          <Divider />

          {/* Profile link */}
          <MenuItem onClick={() => handleNavigate('/profiles/profile')}>
            <ListItemIcon>
              <User size={18} />
            </ListItemIcon>
            Your Profile
          </MenuItem>

          {/* Admin link (only visible for admins and superadmins) */}
          {(userRole === 'admin' || userRole === 'superadmin') && (
            <MenuItem onClick={() => handleNavigate('/profiles/admin')}>
              <ListItemIcon>
                <Users size={18} />
              </ListItemIcon>
              Admin Dashboard
            </MenuItem>
          )}

          {/* Billing link - now visible for all users */}
          <MenuItem onClick={() => {
            // Different routes based on user role
            if (userRole === 'admin' || userRole === 'superadmin') {
              handleNavigate('/profiles/admin/billing');
            } else {
              handleNavigate('/profiles/billing');
            }
          }}>
            <ListItemIcon>
              <CreditCard size={18} />
            </ListItemIcon>
            Billing & Subscriptions
          </MenuItem>

          {/* Settings link */}
          <MenuItem onClick={() => handleNavigate('/profiles/profile')}>
            <ListItemIcon>
              <Settings size={18} />
            </ListItemIcon>
            Settings
          </MenuItem>

          {/* Help link */}
          <MenuItem onClick={() => handleNavigate('/profiles/help')}>
            <ListItemIcon>
              <CircleHelpIcon size={18} />
            </ListItemIcon>
            Help Center
          </MenuItem>
          {/* Additional Help options */}
          <Box sx={{ pl: 2 }}>
            <MenuItem onClick={handleHelpFAQs}>
              <Typography variant="body2" sx={{ pl: 4 }}>
                FAQs
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleGettingStarted}>
              <Typography variant="body2" sx={{ pl: 4 }}>
                Getting Started
              </Typography>
            </MenuItem>
          </Box>


          <Divider />

          {/* Logout */}
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogOut size={18} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardHeader;